import _, {chain, intersection, minBy, union} from 'lodash';
import {createSelector} from 'reselect';

import {
  findTeammateIdsWithInboxAccess,
  hasTrackerSeenAll,
  isTeamInbox,
  isTracker,
} from '../../../helpers/modelHelpers';
import {
  findContentUpdatedAt,
  findConversationContextNamespace,
  findConversationNamespaces,
  isConversationInTeamContext,
  makeConversationActionContext,
  makeFindConversationActionContext,
  makeFindConversationNamespace,
  selectDraftMessageUidsForConversation,
  sortTrackersForConversation,
} from '../../../helpers/models/conversationHelpers';
import {isDirtyDraftMessage, toDraftSummary} from '../../../helpers/models/messageHelpers';
import {isTeamNamespace} from '../../../helpers/models/namespaceHelpers';
import {tagHasBehaviors} from '../../../helpers/models/tagHelpers';
import {AppParametricSelector, AppSelector, wrapInShallowSelector} from '../../../helpers/reselectHelpers';
import {isSafetyNetSatisfied} from '../../../helpers/safetyNetsHelpers';
import {ActivityModel, isActivityPinned} from '../../../models/api/activityModel';
import {ConversationTeamStatusesEnum} from '../../../models/api/conversationCommonTypes';
import {ConversationBaseModel, ConversationModel} from '../../../models/api/conversationModel';
import {
  ConversationTrackerStagesEnum,
  ConversationTrackerStatusesEnum,
} from '../../../models/api/conversationTrackerModel';
import {DraftSummaryModel} from '../../../models/api/draftSummaryModel';
import {InboxModel} from '../../../models/api/inboxModel';
import {DraftMessageModel, MessageModel, MessageVisualStatusesEnum} from '../../../models/api/messageModel';
import {SafetyNetsEnum} from '../../../models/api/safetyNetModel';
import {TagBehaviorsEnum, TagModel} from '../../../models/api/tagModel';
import {TeammateModel} from '../../../models/api/teammateModel';
import {AppState} from '../../appState';
import {ResourceMap} from '../../appTypes';
import {getHasAutoShareDrafts} from '../../featureFlags/featureFlagsSelectors';
import {CellsListTypesEnum} from '../../metadata/cellsLists/cellsList/cellsListState';
import {getCellsLists} from '../../metadata/cellsLists/cellsListsSelectors';
import {CellsContextSelector} from '../../panels/viewer/conversations/viewerPanelConversationsSelectors';
import {getChannels} from '../channels/channelsSelectors';
import {
  getActivities,
  getComments,
  getConversations,
  getMessages,
  getOfflineDraftCommentUids,
  getOfflineDraftMessageUids,
  getTopics,
} from '../dataSelectors';
import {getInboxes} from '../inboxes/inboxesSelectors';
import {getTags} from '../tags/tagsSelectors';
import {getFindTeammate} from '../teammates/lazyTeammatesSelectors';
import {getTeammates, getUserId} from '../teammates/teammatesSelectors';
import {computeShouldShowTrackers} from './conversationsHelpers';

/*
 * Types.
 */

export type ConversationSelector<P> = AppParametricSelector<P, ConversationModel>;
export type OptionalConversationSelector<P> = AppParametricSelector<P, ConversationModel | undefined>;
export type ConversationsSelector<P> = AppParametricSelector<P, ReadonlyArray<ConversationModel>>;

export type ConversationBaseSelector<P> = AppParametricSelector<P, ConversationBaseModel>;
export type OptionalConversationBaseSelector<P> = AppParametricSelector<P, ConversationBaseModel | undefined>;
export type ConversationBasesSelector<P> = AppParametricSelector<P, ReadonlyArray<ConversationBaseModel>>;

/*
 * Base selectors.
 */

export function makeGetConversationById(id: number): AppSelector<ConversationModel | undefined> {
  return (state) => getConversations(state)[id];
}

export function makeGetConversationContentUpdatedAt<P>(getConversation: OptionalConversationSelector<P>) {
  return createSelector(
    [getConversation, getUserId],
    (conversation, userId) => conversation && findContentUpdatedAt(conversation.contentUpdatedDates, userId),
  );
}

/*
 * Trackers.
 */

/** Extract the valid trackers from a conversation, and sort them. */
export function makeGetSortedTrackers<P>(getConversation: ConversationSelector<P>) {
  return createSelector(getConversation, (conversation) => sortTrackersForConversation(conversation));
}

export function makeGetNumberOfParticipants<P>(getConversation: OptionalConversationSelector<P>) {
  return createSelector([getConversation, getUserId], (conversation, userId) =>
    _(conversation ? conversation.trackers : {})
      .filter((t) => Boolean(t && isTracker(t) && t.teammateId !== userId))
      .compact()
      .size(),
  );
}

export function makeGetFetchedAt<P>(getConversation: ConversationSelector<P>) {
  return createSelector([getConversation, getCellsLists], (conversation, cellsLists) => {
    if (!conversation) {
      return undefined;
    }

    const lastFetchTime =
      _(cellsLists)
        .filter((cellsList) => cellsList.type === CellsListTypesEnum.CONVERSATIONS)
        .flatMap((cellsList) => cellsList.ranges)
        .filter((range) => range.ids.includes(conversation.id))
        .map((range) => range.fetchedAt)
        .max() || 0;

    return Math.max(lastFetchTime, conversation.updatedAt);
  });
}

export type TrackerInfosSelector<P> = AppParametricSelector<P, ReadonlyArray<TrackerInfo>>;
export function makeGetTrackers<P>(
  getConversationsSelector: ConversationBasesSelector<P>,
): TrackerInfosSelector<P> {
  return createSelector([getConversationsSelector, getFindTeammate], (conversations, findTeammate) =>
    _(conversations)
      .map((conversation) => {
        const trackers = sortTrackersForConversation(conversation);

        return chain(trackers)
          .map((tracker): TrackerInfo | undefined => {
            const teammate = findTeammate(tracker.teammateId, 'conversationsSelectors makeGetTrackers');
            if (!teammate) {
              return undefined;
            }

            return {
              teammate,
              hasSeenAll: hasTrackerSeenAll(conversation, tracker),
              hasSnoozed: conversation.reminders.some((r) => r.teammateId === teammate.id),
              hasArchived: tracker.status === ConversationTrackerStatusesEnum.ARCHIVED,
              hasMuted: tracker.stage === ConversationTrackerStagesEnum.TRACKER,
              seenAt: tracker.autoSeenAt,
            };
          })
          .compact()
          .sortBy(
            (info) => info.hasMuted,
            (info) => !info.hasSeenAll,
            (info) => info.hasArchived,
            (info) => info.hasSnoozed,
            (info) => -(info.seenAt || 0),
          )
          .value();
      })
      .flatten()
      .uniqBy((t) => t.teammate.id)
      .value(),
  );
}

export interface TrackerInfo {
  teammate: TeammateModel;
  hasSeenAll: boolean;
  hasSnoozed: boolean;
  hasArchived: boolean;
  hasMuted: boolean;
  seenAt: number | undefined;
}

// TODO: Use this selector on desktop.
export function makeGetVisibleTrackers<P>(
  getConversation: ConversationSelector<P>,
  getTrackers: TrackerInfosSelector<P>,
  maxTrackersCount: number,
): TrackerInfosSelector<P> {
  return createSelector([getConversation, getTrackers], (conversation, trackers) => {
    const trackersCount = trackers.length;

    if (!computeShouldShowTrackers(conversation, trackers)) {
      return [];
    }

    // Find the trackers to show.
    const visibleTrackersCount = trackersCount > maxTrackersCount ? maxTrackersCount - 1 : maxTrackersCount;
    const visibleTrackers = _(trackers)
      .take(visibleTrackersCount)
      .reverse() // We render in reverse order for the z-index to be correct.
      .value();

    return visibleTrackers;
  });
}

/*
 * Draft messages.
 */

export function makeGetDraftSeeds<P>(getConversation: ConversationSelector<P>) {
  return createSelector([getConversation], (conversation) => conversation.draftSeeds);
}

export const getOfflineDraftMessagesByConversationId = createSelector(
  [getMessages, getOfflineDraftMessageUids],
  (messages, draftMessageUids) =>
    _(draftMessageUids)
      .map((uid) => messages[uid])
      .compact()
      .groupBy((m) => m.conversationId || 0)
      .value(),
);

export function makeGetDraftMessages<P>(getConversation: OptionalConversationSelector<P>) {
  return wrapInShallowSelector(
    createSelector(
      [getMessages, getOfflineDraftMessagesByConversationId, getConversation],
      (messages, offlineDraftMessagesByConversationId, conversation) => {
        if (!conversation) {
          return [];
        }

        return _(selectDraftMessageUidsForConversation(conversation, offlineDraftMessagesByConversationId))
          .map((uid) => {
            const message = messages[uid];
            return message && message.visualStatus !== MessageVisualStatusesEnum.TIMELINE
              ? message
              : undefined;
          })
          .compact()
          .sortBy((message) => message.sentAt)
          .value();
      },
    ),
  );
}

export type LoadedDraftMessagesSelector<P> = AppParametricSelector<P, ReadonlyArray<DraftMessageModel>>;

export function makeGetLoadedDraftMessages<P>(
  getConversation: ConversationSelector<P>,
): LoadedDraftMessagesSelector<P> {
  const getDraftMessages = makeGetDraftMessages(getConversation);
  return createSelector(getDraftMessages, (draftMessages) =>
    _(draftMessages)
      .map((m) => m.visualStatus === MessageVisualStatusesEnum.DRAFT && m)
      .compact()
      .value(),
  );
}

export function makeGetUserDraftComposeMessages<P>(
  getDraftMessages: LoadedDraftMessagesSelector<P>,
): LoadedDraftMessagesSelector<P> {
  return createSelector([getDraftMessages, getUserId], (draftMessages, userId) =>
    draftMessages.filter((d) => d.authorId === userId),
  );
}

export type LastDraftMessageSummarySelector<P> = AppParametricSelector<P, DraftSummaryModel | undefined>;

export function getConversationLastDraftMessageSummary(
  conversation: ConversationModel,
  draftMessages: ReadonlyArray<MessageModel>,
  hasAutoShareDrafts: boolean,
) {
  // If we have any drafts with client-side changes, calculate the last draft summary client-side using the same logic
  // as getVisibleDraftSeedFor() on the server.
  const hasDirtyDraftMessage = draftMessages.some(isDirtyDraftMessage);
  const lastDraftMessage = minBy(draftMessages, (message) => message.updatedAt);
  if (hasDirtyDraftMessage && lastDraftMessage) {
    return toDraftSummary(lastDraftMessage, conversation, hasAutoShareDrafts);
  }

  // Otherwise, use the server-provided last draft summary.
  return conversation.lastDraft;
}

export function makeGetLastDraftMessageSummary<P>(
  getConversation: ConversationSelector<P>,
): LastDraftMessageSummarySelector<P> {
  const getDraftMessages = makeGetDraftMessages(getConversation);
  return createSelector(
    [getConversation, getDraftMessages, getHasAutoShareDrafts],
    getConversationLastDraftMessageSummary,
  );
}

/*
 * Draft comments.
 */

const getOfflineDraftCommentsByConversationId = createSelector(
  [getComments, getOfflineDraftCommentUids],
  (comments, draftCommentUids) =>
    _(draftCommentUids)
      .map((uid) => comments[uid])
      .compact()
      .groupBy((c) => c.conversationId || 0)
      .value(),
);

function makeGetOfflineDraftComments<P>(getConversation: OptionalConversationSelector<P>) {
  return createSelector(
    [getConversation, getOfflineDraftCommentsByConversationId],
    (conversation, offlineDrafts) => (conversation && offlineDrafts[conversation.id]) || [],
  );
}

function makeGetOnlineDraftComments<P>(getConversation: OptionalConversationSelector<P>) {
  return createSelector([getConversation, getComments], (conversation, comments) =>
    _(conversation?.draftCommentUids ?? [])
      .map((uid) => comments[uid])
      .compact()
      .value(),
  );
}

export function makeGetDraftComments<P>(getConversation: OptionalConversationSelector<P>) {
  const getOfflineDraftComments = wrapInShallowSelector(makeGetOfflineDraftComments(getConversation));
  const getOnlineDraftComments = wrapInShallowSelector(makeGetOnlineDraftComments(getConversation));

  return createSelector([getOfflineDraftComments, getOnlineDraftComments], (offlineDrafts, onlineDrafts) =>
    _(offlineDrafts)
      .concat(onlineDrafts)
      .uniq()
      .sortBy((c) => c.sentAt)
      .value(),
  );
}

/*
 * Inboxes.
 */

export type InboxesSelector<P> = AppParametricSelector<P, ReadonlyArray<InboxModel>>;
export type TeammateIdsSelector<P> = AppParametricSelector<P, ReadonlyArray<TeammateModel['id']>>;
export type IsTeamConversationSelector<P> = AppParametricSelector<P, boolean>;

export function makeGetConversationInboxTeammateIds<P>(
  getConversationInboxes: InboxesSelector<P>,
): TeammateIdsSelector<P> {
  return createSelector([getConversationInboxes, getTeammates], (inboxes, teammates) =>
    _(inboxes)
      .map((i) => findTeammateIdsWithInboxAccess(teammates, i))
      .flatten()
      .uniq()
      .value(),
  );
}

export function makeGetIsTeamConversation<P>(
  getConversationInboxes: InboxesSelector<P>,
): IsTeamConversationSelector<P> {
  return createSelector(getConversationInboxes, (inboxes) => inboxes.some((i) => isTeamInbox(i)));
}

export function makeGetConversationPinnedActivities<P>(
  getConversation: OptionalConversationSelector<P>,
  getValidActivities: AppSelector<ResourceMap<ActivityModel>> = getActivities,
) {
  return createSelector(
    [getConversation, getValidActivities],
    (conversation: ConversationModel | undefined, activities: ResourceMap<ActivityModel>) => {
      if (!conversation) {
        return [];
      }

      return _(activities)
        .values()
        .compact()
        .filter((activity) => activity.conversationId === conversation.id && isActivityPinned(activity))
        .sortBy((activity) => activity.date)
        .value();
    },
  );
}

export function makeGetConversationChannels<P>(getConversation: OptionalConversationSelector<P>) {
  return createSelector([getConversation, getChannels], (conversation, channels) => {
    if (!conversation) {
      return [];
    }

    return _(conversation.channelIds)
      .map((channelId) => channels[channelId])
      .compact()
      .value();
  });
}

export function makeGetConversationInboxes<P>(getConversation: OptionalConversationBaseSelector<P>) {
  return createSelector([getConversation, getInboxes], (conversation, inboxes) =>
    _(conversation?.inboxIds || [])
      .map((inboxId) => inboxes[inboxId])
      .compact()
      .value(),
  );
}

export function makeGetConversationsInboxes<P>(getCurrentConversations: ConversationBasesSelector<P>) {
  return createSelector([getCurrentConversations, getInboxes], (conversations, inboxes) => {
    const commonInboxIds = intersection(...conversations.map((c) => c.inboxIds));
    const [commonTeamInboxes, commonPrivateInboxes] = _(commonInboxIds)
      .map((id) => inboxes[id])
      .compact()
      .partition(isTeamInbox)
      .value();

    // If we have team inboxes in common, show those.
    if (commonTeamInboxes.length > 0) {
      return commonTeamInboxes;
    }

    // If we have private inboxes in common, show those.
    if (commonPrivateInboxes.length > 0) {
      return commonPrivateInboxes;
    }

    // There are no common inboxes so return all the inboxes of selected conversations.
    const allInboxIds = union(...conversations.map((c) => c.inboxIds));
    return _(allInboxIds)
      .map((id) => inboxes[id])
      .compact()
      .value();
  });
}

export function makeGetConversationsNamespaces<P>(getConversationsSelector: ConversationBasesSelector<P>) {
  return createSelector([getConversationsSelector], (conversations) =>
    findConversationNamespaces(conversations),
  );
}

export function makeGetConversationTeamInboxes<P>(
  getConversationSelector: OptionalConversationBaseSelector<P>,
) {
  return createSelector(makeGetConversationInboxes(getConversationSelector), (conversationInboxes) =>
    conversationInboxes.filter(isTeamInbox),
  );
}

export function makeGetConversationIsSpam<P>(getConversation: OptionalConversationBaseSelector<P>) {
  return createSelector([getUserId, getConversation], (userId, conversation) => {
    if (!conversation) {
      return false;
    }

    const isTeamStatusSpam = conversation.teamStatus === ConversationTeamStatusesEnum.SPAM;

    const tracker = conversation.trackers[userId];
    const isTrackerStatusSpam = tracker ? tracker.status === ConversationTrackerStatusesEnum.SPAM : false;

    return isTeamStatusSpam || isTrackerStatusSpam;
  });
}

export function makeGetAreAllInTeamContext<P>(
  getCellsContext: CellsContextSelector<P>,
  getCurrentConversations: ConversationBasesSelector<P>,
) {
  return createSelector([getCellsContext, getCurrentConversations], (cellsContext, conversations) =>
    conversations.every((c) => isConversationInTeamContext(cellsContext, c)),
  );
}

export function makeGetConversationContextNamespaces<P>(
  getCellsContext: CellsContextSelector<P>,
  getCurrentConversations: ConversationBasesSelector<P>,
) {
  return createSelector([getCellsContext, getCurrentConversations], (cellsContext, conversations) =>
    _(conversations)
      .map((conversation) => findConversationContextNamespace(cellsContext, conversation))
      .uniq()
      .value(),
  );
}

type IsInTeamContextSelector<P> = AppParametricSelector<P, boolean>;
export function makeGetIsInTeamContext<P>(
  getCellsContext: CellsContextSelector<P>,
  getSelectedConversations: ConversationBasesSelector<P>,
): IsInTeamContextSelector<P> {
  return createSelector([getCellsContext, getSelectedConversations], (cellsContext, conversations) =>
    conversations.some((c) => isConversationInTeamContext(cellsContext, c)),
  );
}

/** Find the common tags within the selected conversations. */
export function makeGetTags<P>(
  getTeammate: AppParametricSelector<P, TeammateModel>,
  getSelectedConversations: ConversationBasesSelector<P>,
  getIsInTeamContext: IsInTeamContextSelector<P>,
) {
  return createSelector(
    [getTeammate, getSelectedConversations, getIsInTeamContext, getTags],
    (teammate, conversations, isInTeamContext, tags) => {
      const commonTagIds = intersection(...conversations.map((c) => c.tagIds));
      const computeIsTagInTeammateNamespace = (tag: TagModel) => tag.namespace === teammate.namespace;
      return (
        _(commonTagIds)
          .map((id) => tags[id])
          .compact()
          // Only display tags that can be displayed.
          .filter((tag) => tagHasBehaviors(tag, TagBehaviorsEnum.CAN_DISPLAY))
          // Only display private tags from the current teammate.
          .filter((tag) => isTeamNamespace(tag.namespace) || tag.namespace === teammate.namespace)
          // If we're in a team context, remove the inbox tag.
          .filter((tag) => !isInTeamContext || !tag.isMainInboxTag)
          // Display private tags first. Display the inbox tag first.
          .sortBy(
            (tag) => !computeIsTagInTeammateNamespace(tag),
            (tag) => !tag.isMainInboxTag,
          )
          .value()
      );
    },
  );
}

/** Find the common topics within the selected conversations. */
export function makeGetTopics<P>(getCurrentConversations: ConversationBasesSelector<P>) {
  return createSelector([getCurrentConversations, getTopics], (conversations, topics) =>
    _(conversations)
      .flatMap((c) => c.topicIds)
      .uniq()
      .map((topicId) => topics[topicId])
      .compact()
      .value(),
  );
}

export function makeGetConversationTopics<P>(getConversation: OptionalConversationBaseSelector<P>) {
  return createSelector([getConversation, getTopics], (conversation, topics) =>
    _(conversation?.topicIds ?? [])
      .map((topicId) => topics[topicId])
      .compact()
      .value(),
  );
}

// TODO(ldemarne) Use conversation selectors instead of conversation ids in safety net functions below.
export function makeGetConversationSafetyNets(conversationId: number) {
  const getConversationById = makeGetConversationById(conversationId);

  return (state: AppState) => {
    const conversation = getConversationById(state);
    return !conversation ? undefined : conversation.safetyNets ?? [];
  };
}

export function makeAreConversationSafetyNetsSatisfied(
  conversationId: number,
  safetyNetType: SafetyNetsEnum,
) {
  const getConversationSafetyNets = makeGetConversationSafetyNets(conversationId);

  return (state: AppState) => {
    const safetyNets = getConversationSafetyNets(state);
    if (!safetyNets) {
      return true;
    }

    return safetyNets.every((safetyNet) => isSafetyNetSatisfied(safetyNet, safetyNetType));
  };
}

export function makeGetConversationActionContext<P>(
  getCellsContext: CellsContextSelector<P>,
  getConversation: OptionalConversationBaseSelector<P>,
) {
  return createSelector(
    [getConversation, getCellsContext],
    (conversation, cellsContext) => conversation && makeConversationActionContext(cellsContext, conversation),
  );
}

export function makeGetFindConversationActionContext<P>(
  getCellsContext: CellsContextSelector<P>,
  getCurrentConversations: ConversationBasesSelector<P>,
) {
  return createSelector([getCurrentConversations, getCellsContext], (conversations, cellsContext) =>
    makeFindConversationActionContext(cellsContext, conversations),
  );
}

export function makeGetFindConversationNamespace<P>(getCurrentConversations: ConversationBasesSelector<P>) {
  return createSelector([getCurrentConversations], (conversations) =>
    makeFindConversationNamespace(conversations),
  );
}
